import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave, MdEdit } from 'react-icons/md';
import './AddCustomer.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import { VIEW_CUSTOMER_API, UPDATE_CUSTOMER_API } from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const EditCustomer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Detect if we are in 'view' mode (from path or state)
    const [isViewMode, setIsViewMode] = useState(
        location.pathname.includes('/view/') || location.state?.mode === 'view'
    );
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [formData, setFormData] = useState({
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerAddress: ''
    });

    useEffect(() => {
        const fetchCustomerData = async () => {
            try {
                setFetchingData(true);
                const token = localStorage.getItem('accessToken');

                // Fetch from API
                const response = await axios.get(VIEW_CUSTOMER_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    const c = response.data.data;
                    setFormData({
                        customerName: c.customerName || '',
                        customerPhone: c.customerPhone || '',
                        customerEmail: c.customerEmail || '',
                        customerAddress: c.customerAdd || ''
                    });
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to fetch customer details', 'error');
                    navigate('/customers');
                }
            } catch (error) {
                console.error('Error fetching customer details:', error);
                navigate('/customers');
            } finally {
                setFetchingData(false);
            }
        };

        fetchCustomerData();
    }, [id, location.state, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewMode) return;

        const result = await Swal.fire({
            title: 'Update Customer?',
            text: 'Do you want to save the changes to this customer?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.put(UPDATE_CUSTOMER_API(id), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Customer details updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/customers');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update customer');
                }
            } catch (error) {
                console.error('Error updating customer:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Updating customer..." />;
    }

    return (
        <div className="add-customer-page">
            <div className="page-header">
                <button onClick={() => navigate('/customers')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewMode ? 'Customer Details' : 'Edit Customer'}</h2>
                {isViewMode && (
                    <button onClick={() => setIsViewMode(false)} className="edit-toggle-btn" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        <MdEdit /> Edit Mode
                    </button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="customer-form">
                <div className="form-section">
                    <h3>Customer Details</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Customer Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="customerName"
                                value={formData.customerName}
                                onChange={handleChange}
                                placeholder="Enter customer name"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Phone No <span className="required-star">*</span></label>
                            <input
                                type="tel"
                                name="customerPhone"
                                value={formData.customerPhone}
                                onChange={handleChange}
                                placeholder="Enter phone number"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                name="customerEmail"
                                value={formData.customerEmail}
                                onChange={handleChange}
                                placeholder="Enter email address"
                                disabled={isViewMode}
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>Customer Address</label>
                            <textarea
                                name="customerAddress"
                                value={formData.customerAddress}
                                onChange={handleChange}
                                placeholder="Enter full address"
                                rows="3"
                                disabled={isViewMode}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/customers')} className="cancel-btn">
                        {isViewMode ? 'Back to List' : 'Cancel'}
                    </button>
                    {!isViewMode && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Customer
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditCustomer;
